import PropTypes from "prop-types";
import { useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { alphanumericAmpAndComma, alphanumericAndAmp } from "../../../helpers";
import useIntakeFormStore from "../../../store/IntakeForm.store";
import useUserStore from "../../../store/User.store";
import { maxLengthValidation } from "../../../utils/helpers/intakeForm";
import Card from "../../Card";
import CheckboxInput from "../../Form/Checkbox";
import EmailInput from "../../Form/EmailInput";
import SelectInput from "../../Form/SelectInput";
import TextInput from "../../Form/TextInput";

const ClientBasicInfoCard = ({ id, headerIcon }) => {
  const { handleSetIntakeFormStates } = useIntakeFormStore.getState().actions;
  const [formState, formOptions] = useIntakeFormStore((state) => [
    state.data.formState,
    state.data.formOptions,
  ]);
  const { control, setValue } = useFormContext();
  const [isInternalUser, setIsInternalUser] = useState(false);
  const user = useUserStore((state) => state.data.user);
  const isLocked = formState.isFormLocked;

  const clientName = useWatch({
    control,
    name: "client",
  });
  const advisorSponsor = useWatch({
    control,
    name: "advisorSponsor",
  });
  const advisorEmail = useWatch({
    control,
    name: "advisorEmail",
  });
  const sendReportToAdvisor = useWatch({
    control,
    name: "sendReportToAdvisor",
  });
  const salesforceId = useWatch({
    control,
    name: "salesforceId",
  });
  const emailCC = useWatch({
    control,
    name: "emailCC",
  });
  const sendReporttoCC = useWatch({
    control,
    name: "sendReporttoCC",
  });
  const territory = useWatch({
    control,
    name: "territory",
  });

  useEffect(() => {
    handleSetIntakeFormStates({
      clientDetails: {
        client: clientName,
        territory,
        advisorEmail,
        advisorSponsor,
        sendReportToAdvisor,
        salesforceId,
        emailCC,
        sendReporttoCC,
      },
    });
  }, [
    clientName,
    handleSetIntakeFormStates,
    advisorEmail,
    advisorSponsor,
    emailCC,
    salesforceId,
    sendReportToAdvisor,
    sendReporttoCC,
    territory,
  ]);

  useEffect(() => {
    setIsInternalUser(user?.sponsor === "INT");
  }, [user]);

  const isInternalUserBySponsor = (sponsor) => {
    const internalList = ["ALS", "INT"];
    return internalList.includes(sponsor);
  };

  useEffect(() => {
    if (!advisorSponsor) return;
    if (isInternalUserBySponsor(advisorSponsor)) {
      setValue("salesforceId", "");
    } else {
      setValue("territory", "");
    }
  }, [advisorSponsor, setValue]);

  const inputFields = useMemo(() => {
    const defaultFields = [
      {
        type: "text",
        name: "client",
        label: "Client name",
        options: {
          placeholder: "First Name, Last Name",
          required: true,
          showAsterisk: true,
          maxLength: maxLengthValidation(50),
          readOnly: isLocked,
          onChange: (e) => {
            const value = e.target.value;
            e.target.value = alphanumericAndAmp(value);
          },
        },
      },
      {
        type: "text",
        name: "account",
        label: "Proposal name",
        options: {
          placeholder: "Enter a title for this account",
          required: true,
          showAsterisk: true,
          maxLength: maxLengthValidation(44),
          readOnly: isLocked,
          onChange: (e) => {
            const value = e.target.value;
            e.target.value = alphanumericAndAmp(value);
          },
        },
      },
      {
        type: "text",
        name: "advisorName",
        label: "Financial advisor(s)",
        options: {
          placeholder: "Advisor 1, advisor 2, advisor 3",
          required: true,
          showAsterisk: true,
          maxLength: maxLengthValidation(50),
          readOnly: isLocked,
          onChange: (e) => {
            const value = e.target.value;
            e.target.value = alphanumericAmpAndComma(value);
          },
        },
      },
      {
        type: "select",
        name: "advisorSponsor",
        label: "Advisor Firm",
        hide: !isInternalUser,
        options: {
          required: true,
          showAsterisk: true,
          readOnly: isLocked,
          items: formOptions.advisorSponsorList || [],
        },
      },
      {
        type: "email",
        name: "advisorEmail",
        hide: !isInternalUser,
        label: "Advisor Email",
        className: "advisorEmail",
        options: {
          placeholder: "john.dev@example.com",
          maxLength: maxLengthValidation(50),
          readOnly: isLocked,
        },
      },
      {
        type: "checkbox",
        name: "sendReportToAdvisor",
        hide: !isInternalUser,
        label: "Advisor Email",
        options: {
          required: true,
          showAsterisk: true,
          maxLength: maxLengthValidation(50),
          readOnly: isLocked,
        },
      },
    ];
    const emailCCField = [
      {
        type: "email",
        name: "emailCC",
        hide: !isInternalUser,
        className: "emailCC",
        label: "Additional CC Email addresses",
        options: {
          placeholder: "john.dev@example.com",
          readOnly: isLocked,
        },
      },
      {
        type: "checkbox",
        name: "sendReporttoCC",
        hide: !isInternalUser,
        label: "Report CC",
        options: {
          required: true,
          showAsterisk: true,
          maxLength: maxLengthValidation(50),
          readOnly: isLocked,
        },
      },
    ];
    const salesforceIdField = [
      {
        type: "text",
        name: "salesforceId",
        label: "Advisor Saleforce Id",
        hide: !isInternalUser,
        options: {
          placeholder: "Advisor Salesforce Id",
          required: true,
          showAsterisk: true,
          maxLength: maxLengthValidation(50),
          readOnly: isLocked,
          onChange: (e) => {
            const value = e.target.value;
            e.target.value = alphanumericAndAmp(value);
          },
        },
      },
    ];
    const territoryField = [
      {
        type: "select",
        name: "territory",
        label: "Territory",
        options: {
          placeholder: "Enter territory",
          readOnly: isLocked,
          items: formOptions.territories || [],
        },
      },
    ];
    if (!isInternalUserBySponsor(advisorSponsor)) {
      defaultFields.push(...emailCCField, ...salesforceIdField);
    } else {
      defaultFields.push(...emailCCField, ...territoryField);
    }
    return defaultFields;
  }, [
    isInternalUser,
    advisorSponsor,
    formOptions.territories,
    formOptions.advisorSponsorList,
    isLocked,
  ]);

  return (
    <Card
      id={id}
      title="Basic Information"
      subtitle="Required Fields"
      headerIcon={headerIcon}
      openByDefault={true}
    >
      <div className="card__content basic-section">
        {inputFields.map((inputField, idx) => {
          if (inputField?.hide === true) {
            return null;
          }
          if (inputField.type === "text") {
            return (
              <TextInput
                key={idx}
                name={inputField.name}
                label={inputField.label}
                options={inputField.options}
              />
            );
          }

          if (inputField.type === "select") {
            return (
              <SelectInput
                key={idx}
                name={inputField.name}
                label={inputField.label}
                options={inputField.options}
                onChange={inputField?.onChange}
              />
            );
          }

          if (inputField.type === "email") {
            let isDisabled = false;

            if (inputField.name === "advisorEmail") {
              isDisabled = advisorEmail === "";

              isDisabled &&
                sendReportToAdvisor &&
                setValue("sendReportToAdvisor", false);
            } else if (inputField.name === "emailCC") {
              isDisabled = emailCC === "";
              isDisabled && sendReporttoCC && setValue("sendReporttoCC", false);
            }

            return (
              <div className="email-parent" key={idx}>
                <EmailInput
                  parentClassName={inputField?.className}
                  key={idx}
                  name={inputField.name}
                  label={inputField.label}
                  isMulti={inputField.name === "emailCC"}
                  options={inputField.options}
                />
                <CheckboxInput
                  type="checkbox"
                  name={inputFields[idx + 1].name}
                  label=""
                  key={idx + 1}
                  options={{
                    disabled: isDisabled,
                    checked: false,
                    readOnly: inputField.options?.readOnly,
                  }}
                  tooltip="Check to receive transition report email when report is approved."
                />
              </div>
            );
          }

          return null;
        })}
      </div>
    </Card>
  );
};

ClientBasicInfoCard.propTypes = {
  id: PropTypes.string,
  headerIcon: PropTypes.string,
};

export default ClientBasicInfoCard;
